// News e servizi dell'area clienti: lo studio scrive, pubblica e mette in evidenza gli articoli che i
// clienti leggono su clienti.crm-jv.com (anche senza accesso). Testo semplice: paragrafi separati da una
// riga vuota, righe che iniziano con "- " diventano un elenco.
import React, { useCallback, useEffect, useState } from 'react';
import axios from 'axios';
import { toast } from 'sonner';
import { Card, CardContent } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Textarea } from '../components/ui/textarea';
import { Badge } from '../components/ui/badge';
import { Dialog, DialogContent, DialogFooter, DialogHeader, DialogTitle } from '../components/ui/dialog';
import { Plus, Pencil, Trash2, Star, Eye, EyeOff, ImagePlus, Settings2 } from 'lucide-react';
import GestioneServizi from '../components/areaClienti/GestioneServizi';
import CampiCinese from '../components/areaClienti/CampiCinese';

const API = `${process.env.REACT_APP_BACKEND_URL}/api/area-clienti-studio`;
const VUOTA = { tipo: 'news', titolo: '', servizio: 'Lo studio', sommario: '', testo: '', pubblicata: false, in_evidenza: false, data_inizio: '', data_fine: '' };

function Immagine({ url, classe = 'w-24 h-16 object-contain bg-slate-100 rounded' }) {
  const [src, setSrc] = useState(null);
  useEffect(() => {
    let link = null;
    if (!url) { setSrc(null); return undefined; }
    axios.get(`${process.env.REACT_APP_BACKEND_URL}${url}`, { responseType: 'blob' })
      .then((r) => { link = URL.createObjectURL(r.data); setSrc(link); }).catch(() => setSrc(null));
    return () => { if (link) URL.revokeObjectURL(link); };
  }, [url]);
  return src ? <img src={src} alt="" className={classe} /> : null;
}

export default function NewsClienti() {
  const [dati, setDati] = useState({ servizi: [], news: [] });
  const [aperta, setAperta] = useState(false);
  const [form, setForm] = useState(VUOTA);
  const [idInModifica, setIdInModifica] = useState(null);
  const [file, setFile] = useState([]);           // immagini nuove da caricare
  const [esistenti, setEsistenti] = useState([]);  // immagini già salvate (modifica)
  const [salvo, setSalvo] = useState(false);
  const [gestione, setGestione] = useState(false);

  const carica = useCallback(async () => {
    try { setDati((await axios.get(`${API}/news`)).data); } catch (err) { toast.error('Errore nel caricamento delle news'); }
  }, []);
  useEffect(() => { carica(); }, [carica]);

  const nuova = () => { setForm(VUOTA); setIdInModifica(null); setFile([]); setEsistenti([]); setAperta(true); };
  const modifica = (n) => {
    setForm({ tipo: n.tipo || 'news', titolo: n.titolo, servizio: n.servizio, sommario: n.sommario, testo: n.testo, pubblicata: n.pubblicata, in_evidenza: n.in_evidenza, data_inizio: n.data_inizio || '', data_fine: n.data_fine || '',
      titolo_zh: n.titolo_zh || '', sommario_zh: n.sommario_zh || '', testo_zh: n.testo_zh || '' });
    setIdInModifica(n.id); setFile([]); setEsistenti(n.immagini || []); setAperta(true);
  };
  const salva = async () => {
    setSalvo(true);
    try {
      const id = idInModifica
        ? (await axios.put(`${API}/news/${idInModifica}`, form)).data.id
        : (await axios.post(`${API}/news`, form)).data.id;
      if (file.length) {
        const fd = new FormData(); file.forEach((f) => fd.append('files', f));
        await axios.post(`${API}/news/${id}/immagini`, fd);
      }
      toast.success(form.pubblicata ? 'News pubblicata: i clienti la vedono' : 'Bozza salvata (i clienti non la vedono)');
      setAperta(false); carica();
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Salvataggio non riuscito');
    } finally { setSalvo(false); }
  };
  const rapido = async (n, campi) => {
    try { await axios.put(`${API}/news/${n.id}`, { ...n, ...campi }); carica(); } catch (err) { toast.error(err.response?.data?.detail || 'Errore'); }
  };
  const togliEsistente = async (url) => {
    if (!window.confirm('Togliere questa immagine dalla news?')) return;
    try {
      await axios.delete(`${process.env.REACT_APP_BACKEND_URL}${url}`);
      setEsistenti(esistenti.filter((x) => x !== url)); carica();
    } catch (err) { toast.error(err.response?.data?.detail || 'Non tolta'); }
  };
  const elimina = async (n) => {
    if (!window.confirm(`Eliminare la news "${n.titolo}"?`)) return;
    try { await axios.delete(`${API}/news/${n.id}`); toast.success('News eliminata'); carica(); } catch (err) { toast.error('Errore'); }
  };

  return (
    <div className="space-y-6">
      <div className="flex flex-wrap items-center justify-between gap-3">
        <div>
          <h1 className="text-3xl font-bold text-slate-900" style={{ fontFamily: 'Manrope, sans-serif' }}>News (area clienti)</h1>
          <p className="text-slate-600 mt-1">Articoli con data (novità, scadenze, agevolazioni) visibili su clienti.crm-jv.com. Le pratiche con i documenti da portare vanno in <b>Catalogo servizi</b>.</p>
        </div>
        <div className="flex gap-2">
          <Button variant="outline" onClick={() => setGestione(true)}><Settings2 className="w-4 h-4 mr-2" /> Gestisci servizi</Button>
          <Button onClick={nuova}><Plus className="w-4 h-4 mr-2" /> Nuova news</Button>
        </div>
      </div>

      {dati.news.length === 0 && (
        <Card><CardContent className="p-6 text-slate-500">Nessuna news. Scrivi la prima, per esempio "Canone RAI: chi può chiedere l'esenzione".</CardContent></Card>
      )}
      <div className="space-y-3">
        {dati.news.map((n) => (
          <Card key={n.id} className={n.pubblicata ? '' : 'opacity-75'}>
            <CardContent className="p-4 flex gap-4 items-start">
              <div className="relative shrink-0">
                <Immagine url={n.immagini?.[0]} />
                {n.immagini?.length > 1 && <span className="absolute -top-1.5 -right-1.5 bg-slate-900 text-white text-[10px] rounded-full px-1.5">{n.immagini.length}</span>}
              </div>
              <div className="flex-1 min-w-0">
                <div className="flex flex-wrap gap-2 items-center">
                  <Badge variant="outline">{n.servizio}</Badge>
                  {n.tipo === 'servizio' && <Badge className="bg-blue-100 text-blue-800">Pagina del servizio</Badge>}
                  {n.scaduta ? <Badge className="bg-red-100 text-red-800">Scaduta (non più visibile)</Badge>
                    : n.programmata ? <Badge className="bg-blue-100 text-blue-800">Programmata</Badge>
                    : n.pubblicata ? <Badge className="bg-emerald-100 text-emerald-800">Pubblicata {n.data}</Badge>
                    : <Badge className="bg-slate-100 text-slate-700">Bozza</Badge>}
                  {n.periodo && <Badge variant="outline">{n.data_inizio ? '' : 'fino al '}{n.periodo}</Badge>}
                  {n.in_evidenza && <Badge className="bg-amber-100 text-amber-800">In evidenza</Badge>}
                </div>
                <div className="font-semibold text-slate-900 mt-1">{n.titolo}</div>
                <div className="text-sm text-slate-600 line-clamp-2">{n.sommario || n.testo}</div>
                <div className="text-xs text-slate-400 mt-1">{n.autore}{n.aggiornata ? ` · modificata il ${n.aggiornata}` : ''}</div>
              </div>
              <div className="flex flex-col gap-1">
                <Button size="sm" variant="outline" onClick={() => modifica(n)}><Pencil className="w-4 h-4" /></Button>
                <Button size="sm" variant="outline" title={n.pubblicata ? 'Togli dalla pubblicazione' : 'Pubblica'}
                  onClick={() => rapido(n, { pubblicata: !n.pubblicata })}>{n.pubblicata ? <EyeOff className="w-4 h-4" /> : <Eye className="w-4 h-4" />}</Button>
                <Button size="sm" variant="outline" title="In evidenza" onClick={() => rapido(n, { in_evidenza: !n.in_evidenza })}>
                  <Star className={`w-4 h-4 ${n.in_evidenza ? 'fill-amber-400 text-amber-500' : ''}`} /></Button>
                <Button size="sm" variant="outline" className="text-red-600" onClick={() => elimina(n)}><Trash2 className="w-4 h-4" /></Button>
              </div>
            </CardContent>
          </Card>
        ))}
      </div>

      <GestioneServizi aperta={gestione} onChiudi={() => setGestione(false)} onCambiato={carica} />
      <Dialog open={aperta} onOpenChange={setAperta}>
        <DialogContent className="max-w-2xl max-h-[90vh] overflow-y-auto">
          <DialogHeader><DialogTitle>{idInModifica ? 'Modifica news' : 'Nuova news'}</DialogTitle></DialogHeader>
          <div className="space-y-3">
            <div>
              <Label>Titolo</Label>
              <Input value={form.titolo} maxLength={120} onChange={(e) => setForm({ ...form, titolo: e.target.value })}
                placeholder="es. Canone RAI 2028: chi può chiedere l'esenzione" />
            </div>
            <div>
              <div className="flex items-center justify-between">
                <Label>Servizio</Label>
                <button type="button" className="text-xs text-blue-700 hover:underline flex items-center gap-1" onClick={() => setGestione(true)}>
                  <Settings2 className="w-3.5 h-3.5" /> aggiungi / togli servizi</button>
              </div>
              <div className="flex flex-wrap gap-1 mt-1">
                {dati.servizi.map((s) => (
                  <Button key={s} type="button" size="sm" variant={form.servizio === s ? 'default' : 'outline'}
                    onClick={() => setForm({ ...form, servizio: s })}>{s}</Button>
                ))}
              </div>
            </div>
            <div>
              <Label>Sommario (2 righe che si vedono nell'elenco)</Label>
              <Textarea rows={2} maxLength={300} value={form.sommario} onChange={(e) => setForm({ ...form, sommario: e.target.value })} />
            </div>
            <div>
              <Label>Testo</Label>
              <Textarea rows={10} value={form.testo} onChange={(e) => setForm({ ...form, testo: e.target.value })}
                placeholder={'Paragrafi separati da una riga vuota.\n- Le righe che iniziano con un trattino\n- diventano un elenco'} />
            </div>
            <CampiCinese form={form} setForm={setForm} campi={[['titolo', 'Titolo'], ['sommario', 'Sommario'], ['testo', 'Testo', true]]} />
            <div>
              <Label className="flex items-center gap-2"><ImagePlus className="w-4 h-4" /> Immagini (facoltative, JPG/PNG, fino a 10)</Label>
              <p className="text-xs text-slate-500 mb-1">Si vedono sotto il testo, intere; il cliente le apre e le scorre a schermo intero.</p>
              {(esistenti.length > 0 || file.length > 0) && (
                <div className="flex flex-wrap gap-2 mb-2">
                  {esistenti.map((url) => (
                    <div key={url} className="relative">
                      <Immagine url={url} classe="w-24 h-24 object-contain bg-slate-100 rounded border" />
                      <button type="button" onClick={() => togliEsistente(url)} className="absolute -top-2 -right-2 bg-red-600 text-white rounded-full w-6 h-6 text-xs">✕</button>
                    </div>
                  ))}
                  {file.map((f, k) => (
                    <div key={`n${k}`} className="relative">
                      <img src={URL.createObjectURL(f)} alt="" className="w-24 h-24 object-contain bg-slate-100 rounded border border-dashed border-blue-400" />
                      <span className="absolute bottom-0 inset-x-0 text-center text-[10px] bg-blue-600 text-white rounded-b">nuova</span>
                      <button type="button" onClick={() => setFile(file.filter((_, j) => j !== k))} className="absolute -top-2 -right-2 bg-red-600 text-white rounded-full w-6 h-6 text-xs">✕</button>
                    </div>
                  ))}
                </div>
              )}
              <Input type="file" multiple accept="image/jpeg,image/png,image/webp"
                onChange={(e) => { const nuovi = Array.from(e.target.files || []); setFile([...file, ...nuovi].slice(0, Math.max(0, 10 - esistenti.length))); e.target.value = ''; }} />
            </div>
            <div className="grid grid-cols-2 gap-3">
              <div>
                <Label>Data inizio (facoltativa)</Label>
                <Input type="date" value={form.data_inizio} onChange={(e) => setForm({ ...form, data_inizio: e.target.value })} />
                <p className="text-xs text-slate-500 mt-1">Prima di questo giorno i clienti non la vedono.</p>
              </div>
              <div>
                <Label>Data fine (facoltativa)</Label>
                <Input type="date" value={form.data_fine} min={form.data_inizio || undefined} onChange={(e) => setForm({ ...form, data_fine: e.target.value })} />
                <p className="text-xs text-slate-500 mt-1">Dopo questo giorno diventa da sola "non pubblicata".</p>
              </div>
            </div>
            <div className="flex gap-4">
              <label className="flex items-center gap-2 text-sm"><input type="checkbox" checked={form.pubblicata}
                onChange={(e) => setForm({ ...form, pubblicata: e.target.checked })} /> Pubblicata (visibile ai clienti)</label>
              <label className="flex items-center gap-2 text-sm"><input type="checkbox" checked={form.in_evidenza}
                onChange={(e) => setForm({ ...form, in_evidenza: e.target.checked })} /> In evidenza</label>
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setAperta(false)}>Annulla</Button>
            <Button onClick={salva} disabled={salvo || form.titolo.trim().length < 3}>{salvo ? 'Salvo…' : 'Salva'}</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
